import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DataTable from "../DataTable/DataTable";
import "../Table/Table.css";
import { useErrorToast } from "../../hooks/useErrorToast";
import axios from "axios";

function InvoiceSummaryTableB({ currentParam }) {
  const showError = useErrorToast();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState(null);

  // 🔹 Date filter state
  const today = dayjs();
  const [rangeType, setRangeType] = useState(""); // no filter by default
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  // 🧭 Auto-fetch when quick filters (non-custom) are selected
  // useEffect(() => {
  //   if (!rangeType || rangeType === "custom") return;

  //   const today = dayjs();
  //   let start, end;

  //   switch (rangeType) {
  //     case "thisMonth":
  //       start = today.startOf("month");
  //       end = today.endOf("month");
  //       break;
  //     case "lastMonth":
  //       start = today.subtract(1, "month").startOf("month");
  //       end = today.subtract(1, "month").endOf("month");
  //       break;
  //     case "last3Months":
  //       start = today.subtract(2, "month").startOf("month");
  //       end = today.endOf("month");
  //       break;
  //     case "last6Months":
  //       start = today.subtract(5, "month").startOf("month");
  //       end = today.endOf("month");
  //       break;
  //     case "last12Months":
  //       start = today.subtract(11, "month").startOf("month");
  //       end = today.endOf("month");
  //       break;
  //     default:
  //       start = null;
  //       end = null;
  //   }

  //   if (start && end) {
  //     setDateRange({ start, end });
  //     fetchInvoiceSummary(start, end);
  //   }
  // }, [rangeType]);

  useEffect(() => {
  const today = dayjs();
  let start = null;
  let end = null;

  // No rangeType selected yet → default fetch all
  if (rangeType === "") {
    setDateRange({ start: null, end: null });
    fetchInvoiceSummary(null, null); // hit API for all data
    return;
  }

  // Skip if custom (handled manually by Apply button)
  if (rangeType === "custom") return;

  switch (rangeType) {
    case "noFilter":
      // No filter explicitly selected → fetch all
      setDateRange({ start: null, end: null });
      fetchInvoiceSummary(null, null);
      return;

    case "thisMonth":
      start = today.startOf("month");
      end = today; // till today, not end of month
      break;

    case "lastMonth":
      start = today.subtract(1, "month").startOf("month");
      end = today.subtract(1, "month").endOf("month");
      break;

    case "last3Months":
      // exclude current month → full 3 previous months
      start = today.subtract(3, "month").startOf("month");
      end = today.subtract(1, "month").endOf("month");
      break;

    case "last6Months":
      start = today.subtract(6, "month").startOf("month");
      end = today.subtract(1, "month").endOf("month");
      break;

    case "last12Months":
      start = today.subtract(12, "month").startOf("month");
      end = today.subtract(1, "month").endOf("month");
      break;

    default:
      start = null;
      end = null;
  }

  if (start && end) {
    setDateRange({ start, end });
    fetchInvoiceSummary(start, end);
  } else {
    fetchInvoiceSummary(null, null);
  }
}, [rangeType]);


  // 🧹 Reset filters
  const handleReset = () => {
    setRangeType("");
    setDateRange({ start: null, end: null });
    fetchInvoiceSummary(); // fetch all data
  };

  // ✅ Apply custom range
  const handleApply = () => {
    if (!dateRange.start || !dateRange.end) {
      showError("Please select both start and end dates.");
      return;
    }

    if (dayjs(dateRange.end).isBefore(dayjs(dateRange.start))) {
      showError("End date cannot be earlier than start date.");
      return;
    }

    fetchInvoiceSummary(dateRange.start, dateRange.end);
  };

  // 🔹 Dynamic label mapping
  const labelMap = {
    remaining_discrepancy: "Remaining Discrepancy",
    settled_discrepancy: "Settled Discrepancy",
    discrepancy: "Discrepancy",
  };
  const label = labelMap[currentParam] || "Summary";

  // 🔹 Dynamic columns
  const getColumns = () => {
    switch (currentParam) {
      case "settled_discrepancy":
        return [
          { field: "country", headerName: "Country", flex: 1 },
          {
            field: "total_adj",
            headerName: "Total Adjustment",
            flex: 1,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
        ];

      case "discrepancy":
        return [
          { field: "country", headerName: "Country", flex: 1 },
          {
            field: "discrepancy_total",
            headerName: "Total Discrepancy",
            flex: 1,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
        ];

      case "remaining_discrepancy":
        return [
          { field: "country", headerName: "Country", flex: 1 },
          {
            field: "final_discrepancy",
            headerName: "Final Discrepancy",
            flex: 1,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
        ];

      default:
        return [
          { field: "country", headerName: "Country", width: 180 },
          {
            field: "converted_total_usd",
            headerName: "Converted Total (USD)",
            width: 180,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
          {
            field: "discrepancy_total",
            headerName: "Discrepancy Total",
            width: 160,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
          {
            field: "final_discrepancy",
            headerName: "Final Discrepancy",
            width: 160,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
          {
            field: "total_adj",
            headerName: "Total Adjustment",
            width: 160,
            renderCell: (params) => `$${params.value?.toLocaleString()}`,
          },
        ];
    }
  };

  // 🔹 Fetch data
  const fetchInvoiceSummary = async (startDate, endDate) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("tokenInv");

      let url = `${process.env.REACT_APP_URL}/get-invoice-country-summary?search_for=${currentParam}`;
      if (startDate && endDate) {
        url += `&start_date=${dayjs(startDate).format("YYYY-MM-DD")}&end_date=${dayjs(endDate).format("YYYY-MM-DD")}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });

      const resData = response?.data;
      setTableData(resData?.data || []);
      setSummary(resData?.summary || {});
    } catch (error) {
      console.error("error", error);
      showError(error?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🧭 Fetch all data initially
  useEffect(() => {
    fetchInvoiceSummary();
  }, [currentParam]);

  // 🔹 Summary cards (no change from before)
const renderSummaryCards = () => {
  if (!summary) return null;

  const periodLabels = {
    "1_month": "1M",
    "3_month": "3M",
    "6_month": "6M",
    "12_month": "12M",
  };

  const getPeriodValue = (period, key) => summary?.period_summary?.[period]?.[key] ?? 0;

  const cardStyle = { flex: '1 1 calc(20% - 16px)', borderRadius: 2, boxShadow: 2 };

  switch (currentParam) {
    case "settled_discrepancy":
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Adjustment
              </Typography>
              <Typography variant="h6">
                ${summary.total_adj?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          {Object.keys(periodLabels).map((period) => (
            <Card sx={cardStyle} key={period}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Total Adjustment ({periodLabels[period]})
                </Typography>
                <Typography variant="h6">
                  ${getPeriodValue(period, "discrepancy_settled").toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      );

    case "discrepancy":
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Discrepancy
              </Typography>
              <Typography variant="h6">
                ${summary.total_discrepancy?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          {Object.keys(periodLabels).map((period) => (
            <Card sx={cardStyle} key={period}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Discrepancy ({periodLabels[period]})
                </Typography>
                <Typography variant="h6">
                  ${getPeriodValue(period, "total_discrepancy").toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      );

    case "remaining_discrepancy":
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Remaining Discrepancy
              </Typography>
              <Typography variant="h6">
                ${summary.total_remaining_discrepancy?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          {Object.keys(periodLabels).map((period) => (
            <Card sx={cardStyle} key={period}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Remaining ({periodLabels[period]})
                </Typography>
                <Typography variant="h6">
                  ${getPeriodValue(period, "remaining_discrepancy").toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      );

    default:
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Converted (USD)
              </Typography>
              <Typography variant="h6">
                ${summary.total_converted_usd?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Discrepancy
              </Typography>
              <Typography variant="h6">
                ${summary.total_discrepancy?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Remaining Discrepancy
              </Typography>
              <Typography variant="h6">
                ${summary.total_remaining_discrepancy?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={cardStyle}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Adjustment
              </Typography>
              <Typography variant="h6">
                ${summary.total_adj?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
  }
};


  return (
    <div style={{ paddingTop: 20 }}>

      {/* 🔹 Summary + Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* {summary && (
            <Box >
              <Typography variant="h6" gutterBottom>
                Summary ({label})
              </Typography>
              {renderSummaryCards()}
            </Box>
          )} */}
      {/* 🔹 Filter Bar */}
      <Box
        sx={{
            mb:2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
   <div style={{ display: "flex", alignItems: "center" }}>
  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
    Summary ({label})
  </Typography>
</div>

        <FormControl size="small" sx={{ minWidth: 180, }}>
          <Select
            displayEmpty
            value={rangeType}
            onChange={(e) => setRangeType(e.target.value)}
          >
            <MenuItem value="">No Filter (All Data)</MenuItem>
            {/* <MenuItem value="thisMonth">This Month</MenuItem> */}
            <MenuItem value="lastMonth">Last Month</MenuItem>
            <MenuItem value="last3Months">Last 3 Months</MenuItem>
            <MenuItem value="last6Months">Last 6 Months</MenuItem>
            <MenuItem value="last12Months">Last 12 Months</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>

        {rangeType === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker
                slotProps={{textField: {size: "small",sx: { width: 150 },},}}
                label="Start Date"
                value={dateRange.start}
                maxDate={today}
                onChange={(v) => setDateRange({ ...dateRange, start: v })}
              />
              <DatePicker
                slotProps={{textField: {size: "small",sx: { width: 150 },},}}
                label="End Date"
                value={dateRange.end}
                minDate={dateRange.start || undefined}
                maxDate={today}
                onChange={(v) => setDateRange({ ...dateRange, end: v })}
              />

              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleApply}
                disabled={!dateRange.start || !dateRange.end}
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Apply
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleReset}
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Reset
              </Button>
            </Stack>
          </LocalizationProvider>
        )}
      </Box>

          <DataTable
            columns={getColumns()}
            rows={tableData.map((item, i) => ({ id: i, ...item }))}
            loading={loading}
          />
        </>
      )}
    </div>
  );
}

export default InvoiceSummaryTableB;
